
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { CalendarDays, CheckCircle } from 'lucide-react';
import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import ReactMarkdown from 'react-markdown';

interface WeeklyChallengeProps {
  title: string;
  description: string;
  daysLeft: number;
  progress: number;
  totalSteps: number;
  onProgressUpdate?: (newProgress: number) => void;
}

const CHALLENGE_STEPS = [
  {
    title: "Identify the Challenge",
    description: "Define a business problem in your organization that could benefit from AI implementation.",
    prompt: "What specific business challenge are you trying to solve with AI?"
  },
  {
    title: "Explore Solutions",
    description: "Research potential AI solutions that could address your identified challenge.",
    prompt: "What AI approaches or technologies might help solve this challenge?"
  },
  {
    title: "Evaluate Resources",
    description: "Assess the resources, data, and skills needed to implement your AI solution.",
    prompt: "What resources (data, skills, tools) do you need to implement this solution?"
  },
  {
    title: "Create Implementation Plan",
    description: "Develop a brief implementation plan with key milestones and stakeholders.",
    prompt: "Outline your implementation plan with key milestones and stakeholders."
  }
];

export const WeeklyChallenge = ({
  title,
  description,
  daysLeft,
  progress,
  totalSteps,
  onProgressUpdate
}: WeeklyChallengeProps) => {
  const { toast } = useToast();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(progress);
  const [response, setResponse] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [activeTab, setActiveTab] = useState('response');

  const handleContinue = () => {
    if (currentStep < totalSteps) {
      setIsDialogOpen(true);
      setActiveTab('response');
    }
  };

  const handleSubmit = async () => {
    if (!response.trim()) {
      toast({
        title: "Response Required",
        description: "Please provide a response to continue.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Get feedback on the user's response
      const { data, error } = await supabase.functions.invoke('provide-exercise-feedback', {
        body: {
          exerciseTitle: CHALLENGE_STEPS[currentStep].title,
          exerciseDescription: CHALLENGE_STEPS[currentStep].description,
          userAnswer: response
        }
      });

      if (error) {
        throw new Error('Failed to get feedback on your response.');
      }

      // Update progress and set feedback
      const newProgress = currentStep + 1;
      setCurrentStep(newProgress);
      if (onProgressUpdate) {
        onProgressUpdate(newProgress);
      }
      
      // Store the feedback and switch to feedback tab
      setFeedback(data.feedback);
      setActiveTab('feedback');
      
      toast({
        title: "Step Completed!",
        description: "Your response has been submitted. See the feedback tab for detailed review.",
      });
    } catch (error) {
      console.error('Error submitting response:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to submit your response.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setResponse('');
    setFeedback('');
  };

  return (
    <>
      <Card className="glass-card border-primary/20">
        <CardHeader className="pb-2 bg-primary/5 rounded-t-lg">
          <div className="flex justify-between items-center">
            <CardTitle className="text-lg">Weekly Challenge</CardTitle>
            <div className="flex items-center text-primary text-sm font-medium">
              <CalendarDays className="h-4 w-4 mr-1" />
              {daysLeft} days left
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          <h3 className="font-semibold mb-1">{title}</h3>
          <p className="text-sm text-muted-foreground mb-4">{description}</p>
          
          <div className="space-y-3">
            {CHALLENGE_STEPS.map((step, index) => (
              <div 
                key={index}
                className="flex items-center"
              >
                <div className={`w-6 h-6 rounded-full flex items-center justify-center mr-3 border ${
                  index < currentStep 
                    ? 'bg-primary text-white border-primary' 
                    : 'border-muted-foreground text-muted-foreground'
                }`}>
                  {index < currentStep ? (
                    <CheckCircle className="h-5 w-5" />
                  ) : (
                    <span className="text-xs">{index + 1}</span>
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">{step.title}</p>
                  {index < currentStep && (
                    <p className="text-xs text-muted-foreground">{step.description}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
        <CardFooter>
          <Button 
            className="w-full" 
            onClick={handleContinue}
            disabled={currentStep >= totalSteps}
          >
            {currentStep >= totalSteps ? 'Challenge Completed' : 'Continue Challenge'}
          </Button>
        </CardFooter>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={handleCloseDialog}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{CHALLENGE_STEPS[currentStep]?.title || 'Continue Challenge'}</DialogTitle>
            <DialogDescription>
              {CHALLENGE_STEPS[currentStep]?.description || 'Complete this step of the weekly challenge.'}
            </DialogDescription>
          </DialogHeader>
          
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="response">Your Response</TabsTrigger>
              <TabsTrigger value="feedback" disabled={!feedback}>Feedback</TabsTrigger>
            </TabsList>
            
            <TabsContent value="response" className="py-4">
              <p className="text-sm font-medium mb-2">{CHALLENGE_STEPS[currentStep]?.prompt}</p>
              <Textarea
                placeholder="Your response..."
                value={response}
                onChange={(e) => setResponse(e.target.value)}
                className="min-h-[150px]"
              />
            </TabsContent>
            
            <TabsContent value="feedback" className="py-4">
              {feedback ? (
                <div className="bg-muted/30 p-4 rounded-md max-h-[400px] overflow-y-auto">
                  <Alert>
                    <AlertDescription>
                      <div className="prose prose-sm max-w-none dark:prose-invert">
                        <ReactMarkdown>
                          {feedback}
                        </ReactMarkdown>
                      </div>
                    </AlertDescription>
                  </Alert>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Submit your response to receive feedback.</p>
              )}
            </TabsContent>
          </Tabs>
          
          <DialogFooter>
            {activeTab === 'response' ? (
              <>
                <Button variant="outline" onClick={handleCloseDialog}>Cancel</Button>
                <Button onClick={handleSubmit} disabled={isSubmitting || !response.trim()}>
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={handleCloseDialog}>Close</Button>
                <Button onClick={() => handleContinue()} disabled={currentStep >= totalSteps}>
                  Continue to Next Step
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
